import { STATISTIC_TYPE } from "types/ReportingDashboard/elements.types"
import type {
  TimeSeriesRawDataPayload,
  NivoLineChartSeries,
  NivoLineChartPoint,
  ConvertedTimeSeriesData,
} from "types/ReportingDashboard/timeseries.types"
import type { RTableContent } from "src/types/engineOutput.types"

/**
 * Detects the data format type
 */
const detectDataFormat = (
  data: (string | number | null)[][]
): "simple" | "horizontal" | "vertical" | "complex" => {
  if (data.length < 2) return "complex"

  const headerRow = data[0]
  const dataRows = data.slice(1)

  // Simple format: 3x2 table with structure like:
  // [["27", "Statistic", null], [null, "perc(%)", 0.551248]]
  if (
    data.length === 2 &&
    headerRow.length === 3 &&
    headerRow[2] === null &&
    dataRows[0][0] === null &&
    typeof dataRows[0][1] === "string" &&
    typeof dataRows[0][2] === "number"
  ) {
    return "simple"
  }

  // Horizontal format: Multiple columns in header, single data row
  // Can be either:
  // 1. [["27", "Statistic", "Online Banking", "Mobile App", ...], [null, "perc(%)", 0.016740, 0.018502, ...]]
  // 2. [["27", "Statistic", "Online Banking", "Mobile App", ...], ["Totals", "perc(%)", 1, 1, 1, ...]]
  if (
    data.length === 2 &&
    headerRow.length > 3 &&
    typeof dataRows[0][1] === "string"
  ) {
    return "horizontal"
  }

  // Vertical format: Multiple rows with categories in first column, single statistic column
  // [["Features", "Statistic", null], ["ANZ", "n", 276], ["BankWest", "n", 67], ...]
  if (
    data.length > 2 &&
    headerRow.length === 3 &&
    headerRow[2] === null &&
    dataRows.length > 1 &&
    dataRows.every(
      (row) =>
        row && row[0] !== null && row[1] !== null && typeof row[2] === "number"
    ) &&
    dataRows.some((row) => row[0] !== "Totals")
  ) {
    return "vertical"
  }

  // Complex format: Traditional crosstab with multiple data rows
  return "complex"
}

/**
 * Processes simple format data (single value per wave)
 */
const processSimpleFormat = (
  data: (string | number | null)[][],
  waveIdentifier: string,
  selectedStatisticType: STATISTIC_TYPE,
  primaryVariablePath: string,
  breakdownVariablePath: string,
  seriesDataMap: Map<string, NivoLineChartPoint[]>,
  allSeriesKeys: Set<string>
): void => {
  const statRow = data[1]
  const statType = statRow[1] as string
  const value = parseFloat(String(statRow[2])) || 0

  // Exact match - no normalization
  if (statType !== selectedStatisticType) {
    console.warn(
      `Statistic type mismatch: expected ${selectedStatisticType}, got ${statType}`
    )
    return
  }

  // Create series name based on variable paths
  const isPrimaryCategorySelected = primaryVariablePath.includes("//")
  const isBreakdownCategorySelected = breakdownVariablePath.includes("//")

  const primaryCategoryLabel = isPrimaryCategorySelected
    ? primaryVariablePath.split("//").pop() || null
    : null
  const breakdownCategoryLabel = isBreakdownCategorySelected
    ? breakdownVariablePath.split("//").pop() || null
    : null

  let seriesName = ""

  if (
    isPrimaryCategorySelected &&
    isBreakdownCategorySelected &&
    primaryCategoryLabel &&
    breakdownCategoryLabel
  ) {
    seriesName = `${primaryCategoryLabel} × ${breakdownCategoryLabel}`
  } else if (isPrimaryCategorySelected && primaryCategoryLabel) {
    seriesName = primaryCategoryLabel
  } else if (isBreakdownCategorySelected && breakdownCategoryLabel) {
    seriesName = breakdownCategoryLabel
  } else {
    const primaryName =
      primaryVariablePath.split("//").pop() ||
      primaryVariablePath.split("/").pop() ||
      "Primary Variable"
    const breakdownName =
      breakdownVariablePath.split("//").pop() ||
      breakdownVariablePath.split("/").pop() ||
      "Breakdown Variable"
    seriesName = `${primaryName} × ${breakdownName}`
  }

  allSeriesKeys.add(seriesName)

  if (!seriesDataMap.has(seriesName)) {
    seriesDataMap.set(seriesName, [])
  }

  seriesDataMap.get(seriesName)?.push({ x: waveIdentifier, y: value })
}

/**
 * Processes horizontal format data (single data row with multiple columns)
 */
const processHorizontalFormat = (
  data: (string | number | null)[][],
  waveIdentifier: string,
  selectedStatisticType: STATISTIC_TYPE,
  primaryVariablePath: string,
  breakdownVariablePath: string,
  seriesDataMap: Map<string, NivoLineChartPoint[]>,
  allSeriesKeys: Set<string>
): void => {
  const headerRow = data[0]
  const dataRow = data[1]

  const statType = dataRow[1] as string

  // Exact match - no normalization
  if (statType !== selectedStatisticType) {
    console.warn(
      `Statistic type mismatch: expected ${selectedStatisticType}, got ${statType}`
    )
    return
  }

  const isPrimaryCategorySelected = primaryVariablePath.includes("//")
  const isBreakdownCategorySelected = breakdownVariablePath.includes("//")

  const primaryCategoryLabel = isPrimaryCategorySelected
    ? primaryVariablePath.split("//").pop() || null
    : null
  const breakdownCategoryLabel = isBreakdownCategorySelected
    ? breakdownVariablePath.split("//").pop() || null
    : null

  // Get column headers (starting from index 2)
  const columnHeaders = headerRow.slice(2)

  // Process each column
  columnHeaders.forEach((columnHeader, columnIndex) => {
    if (!columnHeader || columnHeader === "Totals") return

    // Check if this column matches our breakdown variable selection
    if (
      breakdownCategoryLabel &&
      String(columnHeader) !== breakdownCategoryLabel
    ) {
      return
    }

    const value = parseFloat(String(dataRow[columnIndex + 2])) || 0 // +2 because we skip wave and statistic columns

    // Create series name
    let seriesName = ""
    if (primaryCategoryLabel && breakdownCategoryLabel) {
      seriesName = `${primaryCategoryLabel} × ${breakdownCategoryLabel}`
    } else if (primaryCategoryLabel) {
      seriesName = `${primaryCategoryLabel} × ${columnHeader}`
    } else if (breakdownCategoryLabel) {
      seriesName = breakdownCategoryLabel
    } else {
      seriesName = String(columnHeader)
    }

    allSeriesKeys.add(seriesName)

    if (!seriesDataMap.has(seriesName)) {
      seriesDataMap.set(seriesName, [])
    }

    seriesDataMap.get(seriesName)?.push({ x: waveIdentifier, y: value })
  })
}

/**
 * Processes vertical format data (multiple rows with categories)
 */
const processVerticalFormat = (
  data: (string | number | null)[][],
  waveIdentifier: string,
  selectedStatisticType: STATISTIC_TYPE,
  primaryVariablePath: string,
  breakdownVariablePath: string,
  seriesDataMap: Map<string, NivoLineChartPoint[]>,
  allSeriesKeys: Set<string>
): void => {
  const dataRows = data.slice(1)

  const isPrimaryCategorySelected = primaryVariablePath.includes("//")
  const isBreakdownCategorySelected = breakdownVariablePath.includes("//")

  const primaryCategoryLabel = isPrimaryCategorySelected
    ? primaryVariablePath.split("//").pop() || null
    : null
  const breakdownCategoryLabel = isBreakdownCategorySelected
    ? breakdownVariablePath.split("//").pop() || null
    : null

  // Process each data row
  for (const row of dataRows) {
    const rowCategory = row[0] // e.g., "Online Banking", "Mobile App", etc.
    const statType = row[1] as string

    if (!rowCategory || rowCategory === "Totals" || !statType) continue

    // Exact match - no normalization
    if (statType !== selectedStatisticType) continue

    // Check if this row matches our breakdown variable selection
    if (
      breakdownCategoryLabel &&
      String(rowCategory) !== breakdownCategoryLabel
    ) {
      continue
    }

    const value = parseFloat(String(row[2])) || 0 // Value is in third column

    // Create series name
    let seriesName = ""
    if (primaryCategoryLabel && breakdownCategoryLabel) {
      seriesName = `${primaryCategoryLabel} × ${breakdownCategoryLabel}`
    } else if (primaryCategoryLabel) {
      seriesName = `${primaryCategoryLabel} × ${rowCategory}`
    } else if (breakdownCategoryLabel) {
      seriesName = breakdownCategoryLabel
    } else {
      seriesName = String(rowCategory)
    }

    allSeriesKeys.add(seriesName)

    if (!seriesDataMap.has(seriesName)) {
      seriesDataMap.set(seriesName, [])
    }

    seriesDataMap.get(seriesName)?.push({ x: waveIdentifier, y: value })
  }
}

/**
 * Processes complex crosstab format data
 */
const processComplexFormat = (
  data: (string | number | null)[][],
  waveIdentifier: string,
  selectedStatisticType: STATISTIC_TYPE,
  primaryVariablePath: string,
  breakdownVariablePath: string,
  seriesDataMap: Map<string, NivoLineChartPoint[]>,
  allSeriesKeys: Set<string>
): void => {
  const headerRow = data[0]
  const isPrimaryCategorySelected = primaryVariablePath.includes("//")
  const isBreakdownCategorySelected = breakdownVariablePath.includes("//")

  const primaryCategoryLabel = isPrimaryCategorySelected
    ? primaryVariablePath.split("//").pop() || null
    : null
  const breakdownCategoryLabel = isBreakdownCategorySelected
    ? breakdownVariablePath.split("//").pop() || null
    : null

  // Find the statistic column (should be index 1)
  const statisticColumnIndex = 1

  // Get column headers (age groups in your example)
  const columnHeaders = headerRow.slice(2) // Skip wave and "Statistic" columns

  // Process each data row (excluding header)
  const dataRows = data.slice(1)

  for (const row of dataRows) {
    const rowCategory = row[0] // e.g., "Male", "Female", "Non-binary"
    const statType = row[statisticColumnIndex] // Should be "n", "perc(%)", etc.

    if (!rowCategory || rowCategory === "Totals" || !statType) continue

    // Exact match - no normalization
    if (String(statType) !== selectedStatisticType) continue

    // Check if this row matches our primary variable selection
    if (primaryCategoryLabel && String(rowCategory) !== primaryCategoryLabel) {
      continue
    }

    // Process each column value
    columnHeaders.forEach((columnHeader, columnIndex) => {
      if (!columnHeader || columnHeader === "Totals") return

      // Check if this column matches our breakdown variable selection
      if (
        breakdownCategoryLabel &&
        String(columnHeader) !== breakdownCategoryLabel
      ) {
        return
      }

      const value = parseFloat(String(row[columnIndex + 2])) || 0 // +2 because we skip wave and statistic columns

      // Create series name
      let seriesName = ""
      if (primaryCategoryLabel && breakdownCategoryLabel) {
        seriesName = `${primaryCategoryLabel} × ${breakdownCategoryLabel}`
      } else if (primaryCategoryLabel) {
        seriesName = `${primaryCategoryLabel} × ${columnHeader}`
      } else if (breakdownCategoryLabel) {
        seriesName = `${rowCategory} × ${breakdownCategoryLabel}`
      } else {
        seriesName = `${rowCategory} × ${columnHeader}`
      }

      allSeriesKeys.add(seriesName)

      if (!seriesDataMap.has(seriesName)) {
        seriesDataMap.set(seriesName, [])
      }

      seriesDataMap.get(seriesName)?.push({ x: waveIdentifier, y: value })
    })
  }
}

/**
 * Converts engine output (an array of table results, one per wave)
 * to a format suitable for Nivo line charts.
 */
export const convertEngineOutputToTimeSeries = (
  rawFullTableResults: TimeSeriesRawDataPayload | null | undefined,
  selectedStatisticType: STATISTIC_TYPE,
  primaryVariablePath: string | null | undefined,
  breakdownVariablePath: string | null | undefined
): ConvertedTimeSeriesData => {
  const emptyResult: ConvertedTimeSeriesData = {
    lineChartNivoData: [],
    seriesKeys: [],
    waveIdentifiers: [],
  }

  if (
    !rawFullTableResults ||
    rawFullTableResults.length === 0 ||
    !primaryVariablePath ||
    !breakdownVariablePath
  ) {
    return emptyResult
  }

  const seriesDataMap = new Map<string, NivoLineChartPoint[]>()
  const allSeriesKeys = new Set<string>()
  const waveIdentifiers: string[] = []

  rawFullTableResults.forEach((engineOutputItem) => {
    if (
      !engineOutputItem?.content ||
      !("data" in engineOutputItem.content) ||
      !Array.isArray(engineOutputItem.content.data) ||
      engineOutputItem.content.data.length < 2
    ) {
      console.warn(
        "Skipping invalid engineOutputItem in time series conversion:",
        engineOutputItem
      )
      return
    }

    const rTableContent = engineOutputItem.content as RTableContent
    const data = rTableContent.data

    // Extract wave identifier from the first row, first column
    const waveIdentifier = String(data[0][0])
    if (!waveIdentifier) {
      console.warn(
        "Skipping engineOutputItem with no wave identifier:",
        engineOutputItem
      )
      return
    }

    if (!waveIdentifiers.includes(waveIdentifier)) {
      waveIdentifiers.push(waveIdentifier)
    }

    // Determine format and process accordingly
    const dataFormat = detectDataFormat(data)

    if (dataFormat === "simple") {
      processSimpleFormat(
        data,
        waveIdentifier,
        selectedStatisticType,
        primaryVariablePath,
        breakdownVariablePath,
        seriesDataMap,
        allSeriesKeys
      )
    } else if (dataFormat === "horizontal") {
      processHorizontalFormat(
        data,
        waveIdentifier,
        selectedStatisticType,
        primaryVariablePath,
        breakdownVariablePath,
        seriesDataMap,
        allSeriesKeys
      )
    } else if (dataFormat === "vertical") {
      processVerticalFormat(
        data,
        waveIdentifier,
        selectedStatisticType,
        primaryVariablePath,
        breakdownVariablePath,
        seriesDataMap,
        allSeriesKeys
      )
    } else {
      processComplexFormat(
        data,
        waveIdentifier,
        selectedStatisticType,
        primaryVariablePath,
        breakdownVariablePath,
        seriesDataMap,
        allSeriesKeys
      )
    }
  })

  const finalSeriesKeys = Array.from(allSeriesKeys)

  // Sort wave identifiers
  waveIdentifiers.sort((a, b) => {
    const numA = parseInt(a.replace(/\D/g, ""), 10)
    const numB = parseInt(b.replace(/\D/g, ""), 10)
    if (!isNaN(numA) && !isNaN(numB)) {
      return numA - numB
    }
    return String(a).localeCompare(String(b))
  })

  // Ensure all series have data points for all waves (fill missing with 0)
  finalSeriesKeys.forEach((seriesKey) => {
    const seriesPoints = seriesDataMap.get(seriesKey) || []
    const existingWavesInSeries = new Set(seriesPoints.map((p) => p.x))

    waveIdentifiers.forEach((waveId) => {
      if (!existingWavesInSeries.has(waveId)) {
        seriesPoints.push({ x: waveId, y: 0 })
      }
    })

    // Sort points by wave identifier
    seriesPoints.sort((a, b) => {
      const numA = parseInt(a.x.replace(/\D/g, ""), 10)
      const numB = parseInt(b.x.replace(/\D/g, ""), 10)
      if (!isNaN(numA) && !isNaN(numB)) {
        return numA - numB
      }
      return String(a.x).localeCompare(String(b.x))
    })

    seriesDataMap.set(seriesKey, seriesPoints)
  })

  const lineChartNivoData: NivoLineChartSeries[] = finalSeriesKeys.map(
    (seriesName) => ({
      id: seriesName,
      data: seriesDataMap.get(seriesName) || [],
    })
  )

  // Safety check to prevent infinite loops - only reject if truly empty
  if (
    lineChartNivoData.length === 0 ||
    lineChartNivoData.every((series) => series.data.length === 0)
  ) {
    console.warn(
      "No valid series or data points found after processing - returning empty result to prevent infinite loop"
    )
    return emptyResult
  }

  return {
    lineChartNivoData,
    seriesKeys: finalSeriesKeys,
    waveIdentifiers,
  }
}
